// New template: pick a campaign + initial subject, then POST creates an
// (empty) template and the route bounces to the editor (ADMIN_UI §3.5).

import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { listCampaigns } from '../../lib/api/campaigns.js';
import { createTemplate } from '../../lib/api/templates.js';
import { TEMPLATE_LANGUAGES, type TemplateLanguage } from '../../lib/languages.js';
import { qk } from '../../lib/query-keys.js';
import { Button } from '../../components/ui/button.js';
import { Input } from '../../components/ui/input.js';
import { Label } from '../../components/ui/label.js';
import { Select } from '../../components/ui/select.js';
import { AsyncState } from '../../components/AsyncState.js';

type NewTemplateSearch = { campaign_id?: number };

export const Route = createFileRoute('/_authed/templates/new')({
  component: NewTemplatePage,
  validateSearch: (search: Record<string, unknown>): NewTemplateSearch => {
    const raw = search.campaign_id;
    if (raw === undefined || raw === null || raw === '') return {};
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? { campaign_id: n } : {};
  },
});

const STARTER_MJML = `<mjml>
  <mj-body>
    <mj-section>
      <mj-column>
        <mj-text>Hi {{display_name}}</mj-text>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`;

const STARTER_HTML = '<!doctype html><html><body><p>Hi {{display_name}}</p></body></html>';

function NewTemplatePage() {
  const navigate = useNavigate();
  const { campaign_id: prefilledCampaignId } = Route.useSearch();
  const campaignsQuery = useQuery({
    queryKey: qk.campaigns.list({}),
    queryFn: () => listCampaigns(),
  });
  const [campaignId, setCampaignId] = useState<string>(
    prefilledCampaignId ? String(prefilledCampaignId) : '',
  );
  const [subject, setSubject] = useState('');
  const [language, setLanguage] = useState<TemplateLanguage>('en');

  const create = useMutation({
    mutationFn: () =>
      createTemplate({
        campaign_id: Number(campaignId),
        language,
        grapejs_json: { mjml: STARTER_MJML },
        html: STARTER_HTML,
        subject,
      }),
    onSuccess: (created) => {
      toast.success('Template created');
      void navigate({ to: '/templates/$templateId', params: { templateId: created.id } });
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Create failed'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!campaignId) return;
    create.mutate();
  }

  return (
    <div className="max-w-xl">
      <h2 className="mb-4 text-xl font-semibold text-neutral-900">New template</h2>
      <AsyncState
        query={campaignsQuery}
        isEmpty={(d) => d.campaigns.length === 0}
        empty={
          <p className="text-sm text-neutral-500">
            Create a campaign first — templates belong to one.
          </p>
        }
      >
        {(data) => (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="campaign">Campaign</Label>
              <Select
                id="campaign"
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
                required
              >
                <option value="">— pick a campaign —</option>
                {data.campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.campaign_key})
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="language">Language</Label>
              <Select
                id="language"
                value={language}
                onChange={(e) => setLanguage(e.target.value as TemplateLanguage)}
              >
                {TEMPLATE_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-neutral-500">
                Fixed when the template is created. Players without a template in their language get
                English; a campaign needs an English template to be activated.
              </p>
            </div>
            <div>
              <Label htmlFor="subject">Subject line</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                placeholder="Hi {{display_name}} — your reward is waiting"
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={!campaignId || create.isPending}>
                {create.isPending ? 'Creating…' : 'Create & edit'}
              </Button>
            </div>
          </form>
        )}
      </AsyncState>
    </div>
  );
}
